/**
 * Shared utilities for {@link EventRow}, {@link EventCard} and {@link EventsDataTable}.
 */
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/components/ui/toast';
import { getEventSummaryByEvent } from '@/services/event-summaries.service';
import { toLocalDateString } from '@/utils/format-date-utils';
import {
  DEFAULT_EVENT_FILTERS,
  EVENT_ATTENDANCE_STATUS_CONFIG,
  type EventFilters,
} from './event-configs';
import type { EventMode, EventStatus, ListEventsQuery } from '@/types/event.types';
import { getErrorMessage } from '@/utils/errors-utils';

/** Event list row used by {@link EventRow} / {@link EventCard}. */
export interface EventItem {
  id: string;
  eventDate: Date;
  startTime: string;
  endTime: string;
  status: EventStatus;
  mode?: EventMode;
  recurrenceType?: string;
  recurrenceGroupId?: string | null;
  establishmentName?: string | null;
  establishment?: { name: string };
  activityName?: string | null;
  activity?: {
    id?: string;
    label: string;
    activityTypeName?: string | null;
    activityType?: { label: string };
  };
  activities?: Array<{
    id?: string;
    label: string;
    activityTypeName?: string | null;
  }>;
  _permissions?: Record<string, boolean>;
}

/** Minimal activity shape used to derive list/card activity labels. */
type EventActivityBase = {
  id?: string;
  label: string;
  activityTypeName?: string | null;
  activityType?: { label: string };
};

/** First activity + extras and the type label used by the row/card. */
export interface DerivedEventActivities {
  allActivities: EventActivityBase[];
  firstActivity: EventActivityBase | null;
  extraActivities: EventActivityBase[];
  firstActivityTypeLabel: string | null;
}

/** Which row actions are available from RBAC + event status. */
export interface EventActionState {
  isEditable: boolean;
  isCancelable: boolean;
  isAttendanceEnabled: boolean;
}

/** Formats a HH:MM:SS time string to HH:MM. */
export function formatEventTime(time: string): string {
  if (!time) return '--:--';
  return time.slice(0, 5);
}

/**
 * Formats an event date with a given date-fns format string.
 * Falls back when the value is absent.
 */
export function formatEventDate(eventDate: Date | string | null | undefined, fmt: string): string {
  return eventDate ? format(new Date(eventDate as string), fmt, { locale: fr }) : 'Date inconnue';
}

/** Derives the unified list of activities and helpers for display. */
export function deriveEventActivities(event: EventItem): DerivedEventActivities {
  let allActivities: EventActivityBase[] = [];
  if (event.activities && event.activities.length > 0) {
    allActivities = event.activities;
  } else if (event.activity) {
    allActivities = [event.activity];
  }

  const firstActivity: EventActivityBase | null =
    allActivities[0] ?? (event.activityName ? { label: event.activityName } : null);
  const extraActivities = allActivities.slice(1);

  const firstActivityTypeLabel = firstActivity
    ? (firstActivity.activityTypeName ?? null) ||
      (firstActivity.activityType?.label ?? null) ||
      null
    : null;

  return { allActivities, firstActivity, extraActivities, firstActivityTypeLabel };
}

/**
 * Computes which row actions are available based on resolved permissions
 * and the current event status.
 */
export function getEventActionState(
  event: Pick<EventItem, 'status'>,
  canEdit: boolean,
  canCancel: boolean,
  canAttendance: boolean,
): EventActionState {
  return {
    isEditable: canEdit && event.status !== 'CANCELLED' && event.status !== 'CLOSED',
    isCancelable: canCancel && event.status !== 'CANCELLED' && event.status !== 'CLOSED',
    isAttendanceEnabled: canAttendance && event.status !== 'PLANNED',
  };
}

/** True when any event chip / search / period filter is set. */
export function hasEventActiveFilters(filters: EventFilters): boolean {
  return (
    !!filters.search ||
    (filters.status?.length ?? 0) > 0 ||
    (filters.establishmentIds?.length ?? 0) > 0 ||
    (filters.levelIds?.length ?? 0) > 0 ||
    (filters.activityTypeIds?.length ?? 0) > 0 ||
    !!filters.recurrenceType ||
    (filters.activityIds?.length ?? 0) > 0 ||
    filters.period !== DEFAULT_EVENT_FILTERS.period
  );
}

/** Maps UI event filters to list-query params, excluding pagination. */
function toEventFilterParams(
  filters: EventFilters,
  dateFrom?: string,
  dateTo?: string,
): Omit<ListEventsQuery, 'page' | 'limit'> {
  return {
    dateFrom,
    dateTo,
    status: filters.status,
    establishmentIds: filters.establishmentIds,
    levelIds: filters.levelIds,
    activityTypeIds: filters.activityTypeIds,
    activityId: filters.activityIds?.[0],
    recurrenceType: filters.recurrenceType,
    search: filters.search,
  };
}

/** Map UI filters + pagination to event list query params. */
export function buildEventListParams(
  filters: EventFilters,
  options: { page: number; limit: number; dateFrom?: string; dateTo?: string },
): ListEventsQuery {
  return {
    ...toEventFilterParams(filters, options.dateFrom, options.dateTo),
    page: options.page,
    limit: options.limit,
  };
}

/** Event export params (same filters as the list, no pagination). */
export function buildEventExportParams(
  filters: EventFilters,
  dateFrom?: string,
  dateTo?: string,
): Omit<ListEventsQuery, 'page' | 'limit'> {
  return toEventFilterParams(filters, dateFrom, dateTo);
}

/** Resolves API dateFrom/dateTo from the period filter (or explicit custom dates). */
export function getDateRangeFromFilters(filters: EventFilters): {
  dateFrom: string | undefined;
  dateTo: string | undefined;
} {
  if (filters.dateFrom || filters.dateTo) {
    return { dateFrom: filters.dateFrom, dateTo: filters.dateTo };
  }
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (filters.period) {
    case 'today': {
      const date = toLocalDateString(startOfToday);
      return { dateFrom: date, dateTo: date };
    }
    case 'week': {
      // Monday–Sunday of the current week
      const dateFrom = new Date(startOfToday);
      const daysFromMonday = (dateFrom.getDay() + 6) % 7;
      dateFrom.setDate(dateFrom.getDate() - daysFromMonday);
      const dateTo = new Date(dateFrom);
      dateTo.setDate(dateTo.getDate() + 6);
      return {
        dateFrom: toLocalDateString(dateFrom),
        dateTo: toLocalDateString(dateTo),
      };
    }
    case 'month': {
      const dateFrom = new Date(now.getFullYear(), now.getMonth(), 1);
      const dateTo = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return {
        dateFrom: toLocalDateString(dateFrom),
        dateTo: toLocalDateString(dateTo),
      };
    }
    case 'all':
    default:
      return { dateFrom: undefined, dateTo: undefined };
  }
}

/** Attendance badge class + label from status (present / absent / pending). */
export function getAttendanceDisplay(status?: string | null): { className: string; label: string } {
  if (status && EVENT_ATTENDANCE_STATUS_CONFIG[status]) {
    return EVENT_ATTENDANCE_STATUS_CONFIG[status];
  }
  return EVENT_ATTENDANCE_STATUS_CONFIG._default;
}

/** Central error copy from {@link getErrorMessage}. */
export function getEventTableApiError(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}

/** Load `GET /event-summaries` by event id, then navigate to view/edit. */
export const useNavigateToEventSummary = () => {
  const navigate = useNavigate();

  const navigateToSummary = async (eventId: string, mode: 'view' | 'edit' = 'view') => {
    try {
      const summary = await getEventSummaryByEvent(eventId);

      if (!summary) {
        toast.warning(getErrorMessage('eventSummaryNotReady'));
        return;
      }

      void navigate('/events', {
        state: {
          openSummary: {
            summaryId: summary.id,
            mode,
          },
        },
      });
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return { navigateToSummary };
};
